// Builds a Create Trip w/AI schedule from the AI's place list (2026-10-16).
//
// The AI no longer writes a timed schedule — it only suggests places, each
// with a day number, a time-of-day hint and a visit length (see TripAI.js
// Next(2)). Asking it to also respect opening days, fill the hours and keep
// each city inside its dates made it think 3-4x longer (30-47 s against
// 5-19 s in a timing test) and it still repeated places and invented
// "Walk from X to Y" events. Those rules are applied here instead, with real
// opening hours:
//   - each day is planned inside its window — the day's start/end, or the
//     arrival/departure time on a travel day (one window per city that day);
//   - order within a day: "morning" places first, then the rest by distance,
//     then "sunset"/"evening" ones; lunch around 12:00-14:00, dinner from
//     about 19:00;
//   - a place closed at its turn waits for opening if that fits, otherwise
//     moves to a later window of the same city; one that never fits stays in
//     the guide unscheduled (a swap candidate, like the extras);
//   - leftover time at the end of a window is filled with the nearest extra
//     place (day 0) of that city that is open then.
// Pure apart from the guide's schedule/places it is handed, so it can be
// tested without the dialog.

import { DateTime } from "luxon";
import Place from "../Models/Place";
import Common from "./Common";

export const MEAL_TIMES = ["breakfast", "lunch", "dinner"];
export const BEST_TIMES = ["morning", "afternoon", "sunset", "evening"];
const AVG_STOP_MINUTES = 110; // a typical visit plus travel, for sizing a day
const LUNCH_FROM = 12 * 60, LUNCH_BY = 14 * 60, DINNER_FROM = 19 * 60;
const MIN_FILL_MINUTES = 60; // don't top up a window with less time left than this
const DEFAULT_MINUTES = 90;

const norm = (s) => String(s || "").trim().toLowerCase();

// An AI's best-time-of-day hint ("Morning", " sunset ") → one of BEST_TIMES,
// or "" when it is empty, "any", a meal time or anything else.
export function parseAIBestTime(hint) {
  const t = norm(hint);
  return BEST_TIMES.includes(t) ? t : "";
}

// Sets place.BestTime from an AI's hint — only when the place has none yet:
// a value the person (or an earlier AI run) set is never overwritten. Returns
// true when it set one. Used by every path that makes places from an AI reply.
export function applyAIBestTime(place, hint) {
  if (!place || place.BestTime) return false;
  const t = parseAIBestTime(hint);
  if (!t) return false;
  place.BestTime = t;
  return true;
}
// Pace as words (the AI preference: "slow paced", "relaxed", "fast moving",
// "busy") or as Build My Trip's visit-length multiplier (1.6 very relaxed,
// 1.3 relaxed, 1 normal, 0.75 packed — fewer or more places a day).
const paceFactor = (pace) => {
  const n = Number(pace);
  if (pace !== "" && pace != null && Number.isFinite(n) && n > 0) return 1 / n;
  return /slow|relaxed/i.test(pace || "") ? 0.75 : /fast|busy/i.test(pace || "") ? 1.25 : 1;
};
const minutesOf = (dt) => dt.hour * 60 + dt.minute;
const utcDay = (dt, plusDays = 0) => DateTime.fromObject({ year: dt.year, month: dt.month, day: dt.day }, { zone: "utc" }).plus({ days: plusDays });

// The windows of the trip: one per destination per day, numbered from day 1
// = the first destination's first day. `count` is how many places to ask the
// AI for in that window.
export function planWindows(destinations, { startH = 9, endH = 20, pace = "", meals = {} } = {}) {
  if (!destinations || destinations.length === 0) return [];
  const first = utcDay(destinations[0].dateStart);
  const windows = [];
  destinations.forEach((d, destIdx) => {
    const firstDay = utcDay(d.dateStart), lastDay = utcDay(d.dateEnd);
    for (let date = firstDay; date <= lastDay; date = date.plus({ days: 1 })) {
      let start = startH * 60, end = endH * 60;
      if (date.equals(firstDay)) start = Math.max(start, minutesOf(d.dateStart));
      if (date.equals(lastDay)) end = Math.min(end, minutesOf(d.dateEnd));
      const day = Math.round(date.diff(first, "days").days) + 1;
      let free = end - start;
      if (meals.lunch && start <= LUNCH_FROM && end >= LUNCH_BY) free -= 60;
      if (meals.breakfast && start <= 9 * 60) free -= 45;
      const count = free >= 60 ? Math.max(1, Math.round((free / AVG_STOP_MINUTES) * paceFactor(pace))) : 0;
      windows.push({ day, destIdx, date, start, end, count });
    }
  });
  return windows;
}

// Which window an AI place belongs to: its day, and the destination whose
// city (else country) it names; on a travel day an unnamed city goes to
// whichever destination that day it matches, else the later one's window
// with time to spare.
export function windowFor(entry, windows, destinations) {
  const sameDay = windows.filter((w) => w.day === entry.day);
  if (sameDay.length === 0) return null;
  if (sameDay.length === 1) return sameDay[0];
  const city = norm(entry.city), country = norm(entry.country);
  return sameDay.find((w) => norm(destinations[w.destIdx].city) === city)
    || sameDay.find((w) => norm(destinations[w.destIdx].country) === country && norm(destinations[w.destIdx].city) === country)
    || sameDay.slice().sort((a, b) => (b.end - b.start) - (a.end - a.start))[0];
}

// Nearest-neighbour order, starting from `from` (or the list's first place).
function byDistance(items, from) {
  const left = items.slice();
  const out = [];
  let cur = from || null;
  while (left.length) {
    let best = 0;
    if (cur) {
      let bestKm = Infinity;
      for (let i = 0; i < left.length; i++) {
        const km = kmBetween(cur, left[i].place);
        if (km < bestKm) { bestKm = km; best = i; }
      }
    }
    const [next] = left.splice(best, 1);
    out.push(next);
    cur = next.place;
  }
  return out;
}
function kmBetween(a, b) {
  const r = Math.PI / 180;
  const la1 = parseFloat(a.Lat), la2 = parseFloat(b.Lat), lo1 = parseFloat(a.Longi), lo2 = parseFloat(b.Longi);
  if (![la1, la2, lo1, lo2].every(Number.isFinite)) return 0;
  const x = (lo2 - lo1) * r * Math.cos(((la1 + la2) / 2) * r), y = (la2 - la1) * r;
  return Math.sqrt(x * x + y * y) * 6371;
}

// entries: [{ place (already in the guide), day (0 = extra), time, city, country }]
// Returns { scheduled: [place], unscheduled: [place] }.
export function buildAIDaySchedule({ entries, destinations, schedule, places, itinerary, startH = 9, endH = 20, roundingTo = 15, pace = "", meals = {}, newEvent }) {
  const windows = planWindows(destinations, { startH, endH, pace, meals });
  const step = Math.max(5, Math.min(roundingTo || 15, 15));
  const ceil = (m) => Math.ceil(m / step) * step;
  const travel = (a, b) => (a && b ? Common.estimateTravelTimeMinutes(a, b, step) : 0);
  const iso = (w, m) => w.date.plus({ minutes: m }).toISO();
  const minutesFor = (p) => {
    const n = Number(p.Duration);
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_MINUTES;
  };
  // When `place` can be visited starting at or after `from` inside [.., limit]:
  // the start minute, or null (closed then, or doesn't fit).
  const fit = (w, place, from, limit) => {
    let s = ceil(from);
    const len = minutesFor(place);
    if (s + len > limit) return null;
    const c = Place.getOpeningHoursConflict({ Places: place.ID, Start: iso(w, s), End: iso(w, s + len) }, places);
    if (c.status === "closed" && !c.closedAllDay && c.minutesUntilOpen != null) {
      s = ceil(s + c.minutesUntilOpen);
      if (s + len > limit) return null;
      const c2 = Place.getOpeningHoursConflict({ Places: place.ID, Start: iso(w, s), End: iso(w, s + len) }, places);
      return c2.status === "closed" || c2.status === "closes_during" ? null : s;
    }
    return c.status === "closed" || c.status === "closes_during" ? null : s;
  };
  const scheduled = new Set();
  const add = (w, place, s, subject) => {
    const ev = newEvent();
    ev.Subject = subject || place.Name;
    ev.Body = "";
    ev.Itinerary = itinerary;
    ev.Places = place.ID;
    ev.Start = iso(w, s);
    ev.End = iso(w, s + minutesFor(place));
    schedule.addEvent(ev);
    scheduled.add(place);
    return s + minutesFor(place);
  };

  const byWindow = new Map(windows.map((w) => [w, []]));
  const extras = [];
  const seen = new Set();
  entries.forEach((e) => {
    if (!e.place || seen.has(e.place)) return;
    seen.add(e.place);
    if (!e.day) { extras.push(e); return; }
    const w = windowFor(e, windows, destinations);
    if (w) byWindow.get(w).push(e);
    else extras.push(e);
  });

  let carried = []; // places that didn't fit, tried again in later windows of the same destination
  windows.forEach((w) => {
    const list = byWindow.get(w);
    const meal = (m) => list.find((e) => e.time === m);
    const sights = list.filter((e) => !MEAL_TIMES.includes(e.time));
    const morning = byDistance(sights.filter((e) => e.time === "morning"));
    const mid = byDistance(sights.filter((e) => e.time !== "morning" && e.time !== "sunset" && e.time !== "evening"), morning.length ? morning[morning.length - 1].place : null);
    const late = byDistance(sights.filter((e) => e.time === "sunset" || e.time === "evening"));
    const ordered = [...carried.filter((e) => e.destIdx === w.destIdx), ...morning, ...mid];
    carried = carried.filter((e) => e.destIdx !== w.destIdx);
    let t = w.start, last = null;
    const tryPlace = (e, limit, subject) => {
      const s = fit(w, e.place, t + travel(last, e.place), limit);
      if (s == null) return false;
      t = add(w, e.place, s, subject);
      last = e.place;
      return true;
    };
    const breakfast = meal("breakfast"), lunch = meal("lunch"), dinner = meal("dinner");
    if (breakfast && t <= 10 * 60) tryPlace(breakfast, w.end, `Breakfast: ${breakfast.place.Name}`);
    let lunchDone = !lunch || w.start > LUNCH_BY - 30 || w.end < LUNCH_FROM + 30;
    const lunchNow = (next) => {
      if (lunchDone) return;
      const nextEnd = t + (next ? travel(last, next.place) + minutesFor(next.place) : 0);
      if (t >= LUNCH_FROM || nextEnd > LUNCH_BY) {
        lunchDone = true;
        const from = Math.max(t + travel(last, lunch.place), LUNCH_FROM - 30);
        const s = fit(w, lunch.place, from, Math.min(w.end, LUNCH_BY + 90));
        if (s != null) { t = add(w, lunch.place, s, `Lunch: ${lunch.place.Name}`); last = lunch.place; }
      }
    };
    // the late group keeps its slot at the end of the day
    const lateMinutes = late.reduce((n, e) => n + minutesFor(e.place) + 20, 0);
    ordered.forEach((e) => {
      lunchNow(e);
      if (!tryPlace(e, w.end - lateMinutes)) carried.push({ ...e, destIdx: w.destIdx });
    });
    lunchNow(null);
    // time left before the evening: top up with nearby open extras of this city
    const dest = destinations[w.destIdx];
    const isHere = (e) => norm(e.city) === norm(dest.city) || norm(dest.city) === norm(dest.country) || !dest.city;
    for (;;) {
      if (w.end - lateMinutes - t < MIN_FILL_MINUTES) break;
      const pool = extras.filter((e) => !scheduled.has(e.place) && isHere(e));
      if (!pool.length) break;
      const next = byDistance(pool, last)[0];
      extras.splice(extras.indexOf(next), 1);
      tryPlace(next, w.end - lateMinutes);
    }
    late.forEach((e) => { if (!tryPlace(e, w.end)) carried.push({ ...e, destIdx: w.destIdx }); });
    if (dinner && w.end >= DINNER_FROM - 60) {
      const s = fit(w, dinner.place, Math.max(t + travel(last, dinner.place), DINNER_FROM), 23 * 60);
      if (s != null) { t = add(w, dinner.place, s, `Dinner: ${dinner.place.Name}`); last = dinner.place; }
    }
  });

  const all = [...seen];
  return { scheduled: all.filter((p) => scheduled.has(p)), unscheduled: all.filter((p) => !scheduled.has(p)) };
}

// The AI's reply: an array of rows [name, city, country, category, day,
// time, minutes] — or, defensively, objects with the same fields. Returns
// TripAI's usual entry objects (Attraction/City/Country/Category/Duration)
// plus Day and BestTime; day 0 = an extra (IsBackup).
export function rowsToEntries(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => {
    const o = Array.isArray(r)
      ? { Attraction: r[0], City: r[1], Country: r[2], Category: r[3], Day: r[4], BestTime: r[5], Duration: r[6] }
      : { Attraction: r.Attraction || r.name, City: r.City || r.city, Country: r.Country || r.country, Category: r.Category || r.category, Day: r.Day ?? r.day, BestTime: r.BestTime || r.time, Duration: r.Duration || r.minutes };
    const day = parseInt(o.Day, 10);
    const time = norm(o.BestTime);
    return {
      Attraction: String(o.Attraction || "").trim(),
      City: String(o.City || "").trim(),
      Country: String(o.Country || "").trim(),
      Category: String(o.Category || "").trim(),
      Duration: Number.isFinite(Number(o.Duration)) && Number(o.Duration) > 0 ? Number(o.Duration) : undefined,
      Day: Number.isFinite(day) && day > 0 ? day : 0,
      BestTime: [...BEST_TIMES, ...MEAL_TIMES].includes(time) ? time : "any",
      IsBackup: !(Number.isFinite(day) && day > 0),
      Description: "",
    };
  }).filter((e) => e.Attraction);
}
